import { ReactNode, useEffect, useState } from "react";
import { Datepicker } from "@mobiscroll/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import {
  MRT_EditActionButtons as MRTEditActionButtons,
  MRT_Row,
  MRT_TableInstance
} from "material-react-table";
import { t } from "i18next";
import { DateTime } from "luxon";
import { EquipmentRow, EquipmentRowValues } from "./typings/equipment-row.types";
import { equipmentRoleApiFlags, isStandaloneEquipmentRole } from "./helpers/equipment-role.helpers";
import ShowAssignedFloor from "../show-assigned-floor.component";
import { isDateInRange } from "../../../utils/utilities";
import { FileObject } from "../../../features/FileDropzone/typings/FileObject.type";
import { PictureDropzone2 } from "../../Pickers/picture-dropzone.component(2)";
import { TransitionForSlideoutPanel } from "../../SlideoutPanel/SlideoutPanelTransition.component";

export const NoEquipmentImage = ({ height }: { height: number }) => (
  <Skeleton variant={"rounded"} height={height} width={height} animation={false}>
    <Box width={height} height={height} sx={{ textAlign: "center", alignContent: "center" }}>
      <Typography variant={"body2"} sx={{ visibility: "visible" }}>
        {t("No Image")}
      </Typography>
    </Box>
  </Skeleton>
);

/** display Equipment Image */
export const renderEquipmentImage = (
  picturePath: string | null | undefined,
  height: number,
  setShowBiggerImage?: (image: string) => void
) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
      {picturePath && (
        <Button
          sx={{ p: 0 }}
          variant={"text"}
          disabled={!setShowBiggerImage}
          onClick={e => {
            e.stopPropagation();
            setShowBiggerImage?.(picturePath);
          }}
          data-testid="show-bigger-image-btn"
        >
          <img
            alt="equipment-image"
            height={height}
            width={height}
            src={picturePath} // ?? `https://picsum.photos/${height}/${height}`}
            loading="lazy"
            style={{ borderRadius: "8px" }}
          />
        </Button>
      )}
      {!picturePath && <NoEquipmentImage height={height} />}
    </Box>
  );
};

/** Button to display a workplace in the Facility Manager */
export const renderAssignedButton = (
  row: MRT_Row<EquipmentRow>,
  setSelectedInventoryId: (inventoryId: number) => void,
  setFloorPlan: (floorPlan: any) => void,
  setOpen: (open: boolean) => void,
  setDialogTitle: (title: string) => void
) => {
  // retrieve device data for a given device schedule
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
      <ShowAssignedFloor
        params={row.original}
        setSelectedInventoryId={setSelectedInventoryId}
        setFloorPlan={setFloorPlan}
        setOpen={setOpen}
        setDialogTitle={setDialogTitle}
        noPlaceButtonText={t("Not placed")}
        showButtonText={
          row.original.placeInventoryId == null ? t("Show Zone") : t("Show Workplace")
        }
        type={"Equipment"}
      />
    </Box>
  );
};

export const renderEditDepreciationDate = ({
  header,
  rowOriginal
}: Readonly<{
  header: string;
  rowOriginal: EquipmentRow;
}>) => {
  return <EditEquipmentDepreciationDate header={header} rowOriginal={rowOriginal} />;
};

export const EditEquipmentDepreciationDate = ({
  header,
  rowOriginal
}: Readonly<{
  header: string;
  rowOriginal: EquipmentRow;
}>) => {
  const [tempDate, setTempDate] = useState<string | null>(null);

  const handleRemoveDate = () => {
    setTempDate(null);
    rowOriginal.depreciationDate = "";
  };

  useEffect(() => {
    if (!rowOriginal.depreciationDate) return;
    setTempDate(rowOriginal.depreciationDate);
  }, [rowOriginal.depreciationDate]);

  return (
    <Box
      data-testid="equipment-depreciationDate-picker-box"
      sx={{ display: "flex", alignItems: "center", ml: -2, height: 55 }}
    >
      <Datepicker
        data-testid="equipment-depreciationDate-picker"
        label={header}
        controls={["calendar"]}
        value={tempDate}
        onChange={e => {
          const changed = DateTime.fromJSDate(e.value as Date);
          setTempDate(changed.toISO());
          rowOriginal.depreciationDate = changed.toISO() as string;
        }}
      />
      <Button data-testid="remove-date-btn" onClick={handleRemoveDate}>
        {t("Remove date")}
      </Button>
    </Box>
  );
};

export const renderEditEquipmentImage = ({
  header,
  rowOriginal,
  imageFile,
  setImageFile,
  deleteRowImage,
  setDeleteRowImage
}: Readonly<{
  header: string;
  rowOriginal: EquipmentRow;
  imageFile: { file: FileObject[] };
  setImageFile: (newImageFile: { file: FileObject[] }) => void;
  deleteRowImage: { rowId: number | null; picturePath: string | null };
  setDeleteRowImage: (item: { rowId: number | null; picturePath: string | null }) => void;
}>) => {
  return (
    <EditEquipmentImageComp
      header={header}
      rowOriginal={rowOriginal}
      imageFile={imageFile}
      setImageFile={setImageFile}
      deleteRowImage={deleteRowImage}
      setDeleteRowImage={setDeleteRowImage}
    />
  );
};

export const EditEquipmentImageComp = ({
  header,
  rowOriginal,
  imageFile,
  setImageFile,
  setDeleteRowImage
}: Readonly<{
  header: string;
  rowOriginal: EquipmentRow;
  imageFile: { file: FileObject[] };
  setImageFile: (newImageFile: { file: FileObject[] }) => void;
  deleteRowImage: { rowId: number | null; picturePath: string | null };
  setDeleteRowImage: (item: { rowId: number | null; picturePath: string | null }) => void;
}>) => {
  const [showCurrentImage, setShowCurrentImage] = useState(!!rowOriginal.picturePath);

  const deleteCurrentPicture = () => {
    setShowCurrentImage(false);
    setDeleteRowImage({ rowId: rowOriginal.id, picturePath: null });
  };

  return (
    <Grid2 container>
      <Grid2 size={{ xs: 12, sm: 6 }} sx={{ pr: 2 }}>
        <Typography sx={{ mb: 2 }}>{header}</Typography>
        {showCurrentImage ? (
          renderEquipmentImage(rowOriginal.picturePath, 200)
        ) : (
          <NoEquipmentImage height={200} />
        )}
        <Divider
          sx={{
            mt: 2,
            mb: 2,
            opacity: "0.6"
          }}
        />
        {rowOriginal.picturePath && (
          <Button data-testid="delete-current-image-btn" onClick={deleteCurrentPicture}>
            <Delete sx={{ mr: 0.5 }} fontSize="small" />
            <Typography sx={{ fontSize: "0.9rem" }}>{t("Delete picture")}</Typography>
          </Button>
        )}
      </Grid2>

      <Grid2 size={{ xs: 12, sm: 6 }}>
        <PictureDropzone2
          title={t("Upload an equipment image")}
          files={imageFile.file}
          error={!imageFile.file}
          preview={false}
          onAdd={files => setImageFile({ file: files })}
          onDelete={() => setImageFile({ file: [] })}
          style={{ titleTypography: "body1", parentMarginBottom: 0 }}
        />
      </Grid2>
    </Grid2>
  );
};

export function mapEditComponentsByColumnId(
  row: MRT_Row<EquipmentRow>,
  internalEditComponents: ReactNode[]
): Record<string, ReactNode> {
  const dataCells = row
    .getAllCells()
    .filter(cell => cell.column.columnDef.columnDefType === "data");

  return Object.fromEntries(
    dataCells.map((cell, index) => [cell.column.id, internalEditComponents[index]])
  );
}

export function SlidePanelCreateEdit({
  table,
  row,
  internalEditComponents,
  headline
}: Readonly<{
  table: MRT_TableInstance<EquipmentRow>;
  row: MRT_Row<EquipmentRow>;
  internalEditComponents: ReactNode[];
  headline: string;
}>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const editDialog = headline === t("Edit Equipment");
  const editById = mapEditComponentsByColumnId(row, internalEditComponents);

  const createOrEditDialogStyle = {
    maxWidth: isMobile ? "inherit" : "50vw",
    width: isMobile ? "100%" : "40vw",
    height: "calc(-64px + 100vh)",
    maxHeight: "calc(-64px + 100vh)",
    position: "absolute",
    right: 0,
    margin: 0,
    padding: 3,
    top: "64px"
  };

  return (
    <Dialog
      open
      data-testid="equip_edit_row_dialog"
      slotProps={{ paper: { sx: { ...createOrEditDialogStyle } } }}
      transitionDuration={400}
      slots={{ transition: TransitionForSlideoutPanel }}
    >
      {/* edit equipment dialog contents */}
      <DialogTitle variant="h4">{headline}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1.5rem", mt: 2 }}>
        <Grid2 container data-testid="edit-equip-dialog-contents" columnGap={4}>
          <Grid2 container size={{ xs: 12 }} rowGap={3.5}>
            {editById.picturePath}
            {editById.name}
            {editById.description}
            {editById["equipmentCategory.name"]}
            {editById.assetRefId}
            {editDialog && editById["location.name"]}
            {editDialog && editById["floor.name"]}
            {editById.zoneInventoryId}
            {editById.placeInventoryId}
            {editById.pricePerHour}
            {editById.bookable}
            {editById.bookingLeadTime}
            {editById.servicePermissions}
            {editById.depreciationDate}
          </Grid2>
        </Grid2>
      </DialogContent>
      <Divider sx={{ mt: 3, mb: 3, opacity: "0.6" }} />
      <DialogActions>
        <MRTEditActionButtons variant="text" table={table} row={row} />
      </DialogActions>
    </Dialog>
  );
}
export const renderSimpleCellName = (name?: string) => <Typography>{name ?? ""}</Typography>;
export const renderCellBookable = (row: MRT_Row<EquipmentRow>) => {
  return t(row.original.bookable === "true" ? "Yes" : "No");
};

export function EditEquipmentBookingLeadTime({
  cell,
  column,
  row,
  table,
  validationError,
  onClearValidationError
}: Readonly<{
  cell: { getValue: () => unknown };
  column: { id: string };
  row: MRT_Row<EquipmentRow>;
  table: MRT_TableInstance<EquipmentRow>;
  validationError?: string;
  onClearValidationError?: () => void;
}>) {
  const bookable = (row._valuesCache.bookable as string | undefined) ?? row.original.bookable;
  const enabled = isStandaloneEquipmentRole(bookable);
  const rawValue = cell.getValue();
  const value = rawValue == null || rawValue === "" ? "" : String(rawValue);

  const syncRow = () => {
    const creatingRow = table.getState().creatingRow;
    if (creatingRow?.id === row.id) {
      table.setCreatingRow({ ...row });
    } else {
      table.setEditingRow({ ...row });
    }
  };

  return (
    <TextField
      fullWidth
      type="number"
      label={t("_equipmentManagement_bookingLeadTime")}
      value={value}
      disabled={!enabled}
      error={!!validationError}
      helperText={validationError}
      inputProps={{ min: 0, step: 0.5, "data-testid": "equipment-booking-lead-time-input" }}
      onFocus={() => onClearValidationError?.()}
      onChange={e => {
        row._valuesCache[column.id] = e.target.value;
        syncRow();
      }}
    />
  );
}

export const renderCellDepreciationDate = (row: MRT_Row<EquipmentRow>, dateFormat: string) => {
  if (!row.original.depreciationDate) return null;
  const isDeprecated = DateTime.fromISO(row.original.depreciationDate).diffNow().valueOf() <= 0;
  return (
    <Box>
      {/* if today after depreciation date */}
      {isDeprecated && <>{t("deprecated")}</>}
      {DateTime.fromISO(row.original.depreciationDate).toFormat(dateFormat)}
    </Box>
  );
};

export const renderMasterBookingLeadTimeCell = (bookingLeadTime: number | null) => {
  if (bookingLeadTime == null) {
    return <></>;
  }

  return (
    <>
      {bookingLeadTime} {bookingLeadTime > 1 ? t("Hours") : t("Hour")}
    </>
  );
};

export const serializeEquipment = (
  newVal: EquipmentRowValues,
  rowOriginal: EquipmentRow,
  file: FileObject[] | undefined
) => {
  const {
    name,
    description,
    status,
    picturePath,
    equipmentCategoryId,
    location,
    floor,
    zoneInventoryId,
    placeInventoryId,
    assetRefId,
    pricePerHour,
    bookable,
    bookingLeadTime,
    servicePermissions
  } = newVal;

  const data = new FormData();

  if (rowOriginal.id) data.append("id", String(rowOriginal.id));
  data.append("name", name);
  data.append("description", description);
  data.append("equipmentCategoryId", String(equipmentCategoryId));

  data.append("status", String(status));

  // when add photo - imageFile exists and null rowPic, valPic
  if (file?.length) data.append("file", file[0].file);
  // when delete photo - value.picturePath : current path / rowOriginal.picturePath : undefined => not sending picturePath

  // photo stays
  if (picturePath && rowOriginal.picturePath) data.append("picturePath", String(picturePath));

  if (location) data.append("location", JSON.stringify(location));
  if (floor) data.append("floor", JSON.stringify(floor));

  if (assetRefId) data.append("assetRefId", assetRefId);

  if (pricePerHour) data.append("pricePerHour", String(pricePerHour));

  const roleFlags = equipmentRoleApiFlags(bookable);
  data.append("bookable", String(roleFlags.bookable));
  data.append("standaloneBookable", String(roleFlags.standaloneBookable));

  if (roleFlags.standaloneBookable === 0) {
    if (placeInventoryId) data.append("placeInventoryId", String(placeInventoryId));
    if (zoneInventoryId) data.append("zoneInventoryId", String(zoneInventoryId));
  }

  if (roleFlags.standaloneBookable === 1 && bookingLeadTime != null && bookingLeadTime !== "") {
    data.append("bookingLeadTime", String(bookingLeadTime));
  }

  if (servicePermissions) {
    data.append(
      "permissions",
      JSON.stringify({
        companyIds: servicePermissions.companyIds ?? [],
        costCenterUnitIds: servicePermissions.costCenterUnitIds ?? [],
        userIds: servicePermissions.userIds ?? []
      })
    );
  }

  data.append("depreciationDate", String(rowOriginal.depreciationDate));

  return data;
};

export const handleDepreciationFilterFn = (depreciationDate: string, filterValue: any) => {
  const rowVal = DateTime.fromISO(depreciationDate);

  // no filter applied
  if (filterValue.every((val: any) => val === undefined)) return true;

  // could be invalid one of them
  // min and max
  return isDateInRange(rowVal, [
    DateTime.fromJSDate(new Date(filterValue[0])),
    DateTime.fromJSDate(new Date(filterValue[1]))
  ]);
};
